import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useHistory } from 'react-router-dom'
import { useAppDispatch } from '@/features/hooks'
import { editingSectionActions } from '@/features/reservation-detail/editing-section-slice'
import dayjs from 'dayjs'
import moment from 'moment'
import * as api from '@/apis/aipass'
import { reservationDetailSalesTableStyle } from '@/constants/layout'
import { sectionHeaderStyle } from '@/components/organisms/dashboard/style'
import { Button, ButtonType } from '@/components/atoms/button'
import { NotConfirmedUsageDetail } from '@/components/molecules/customer/sales-manager/usage-detail/not-confirmed-usage-detail'
import { ConfirmedUsageDetail } from '@/components/molecules/customer/sales-manager/usage-detail/confirmed-usage-detail'
import { PostClosingConfirmedUsageDetail } from '@/components/molecules/customer/sales-manager/usage-detail/post-closing-confirmed-usage-detail'
import { PostClosingPaymentDateModal } from '@/components/molecules/customer/sales-manager/usage-detail/post-closing-payment-date-modal'
import { AccountContext } from '@/contexts/account'
import { hasManagerRole } from '@/libs/account'
import { canEditScreen, canUseOperation } from '@/libs/hotel-pms-permission'
import {
  buildPostClosingSalesAdjustmentParams,
  buildPostClosingDeleteParams,
  buildAdminSalesSavePayload,
  canShowPostClosingEditPencil,
  canUsePostClosingSalesApi,
  confirmClosedSalesDateSelection,
  getPostClosingAdjustmentAlertMessage,
  hasPostClosingPaymentDatetime,
  isPostClosingRowInputValid,
  isPostClosingTarget,
  shouldShowPostClosingUsageDetailRow,
  filterUsageDetailsByGuestRoom,
} from '@/libs/post-closing-sales-adjustment'
import type { PostClosingModalSubmitValues } from '@/models/post-closing-sales-adjustment'
import { UsageDetailSubject } from '@/components/molecules/customer/sales-manager/usage-detail/subjects'
import { DropDownButton } from '@/components/molecules/drop-down-button'
import { EditSalesType, PaymentSettingType, SalesType } from '@/models/sales'
import { ReservationType } from '@/models/reservation'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { ApprovedStatus } from '@/models/reservation-approved-status'
import { NOT_ENOUGH_BALANCE_CODE } from '@/errors/company-advance-money-error'
import { isSalesSubjectChange } from '@/libs/sales-subjects'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { InvoiceDetailModal } from '@/components/organisms/invoice-manager/invoice-detail-modal'
import { PaymentSettingType as ModelPaymentSettingType } from '@/models/payment-setting'
import { OnlineBillingModal } from './online-billing-modal'
import { initializeSelectBoxStyle } from '@/constants/form'
import { RoomFilterButton } from '@/components/molecules/customer/sales-manager/usage-detail/room-filter-button'
import { useConfirmModal } from '@/components/organisms/customer/use-confirm-modal'
import { isItemDatesInStayPeriod } from '@/utils/reservation-period'
import { SalesSubjectMasterType, SalesSubjectSetType, SalesSubjectTaxType } from '@/models/sales-manager/sales'
import { isStaffGuardCancelledError } from '@/libs/errors'
import { useSalesEditGuard } from '@/hooks/use-sales-edit-guard'
import { AdvanceCompanyType } from '@/models/sales-manager/advance-money'

const ROOM_CHARGE_SUBJECT_CODE = '101'

type UsageDetailsProps = {
  checkinId?: string
  salesPayments: PaymentSettingType[]
  salesSubjects: SalesSubjectMasterType[]
  salesSubjectSets: SalesSubjectSetType[]
  salesSubjectTaxes: SalesSubjectTaxType[]
  totalUnpaid: number
  originUsageDetails: SalesType[]
  usageDetails: SalesType[]
  setUsageDetails: (v: SalesType[]) => void
  editUsageDetails: EditSalesType[]
  setEditUsageDetails: (v: EditSalesType[]) => void
  confirmedDay: string
  useCheckinDay: string
  setIsLoading: (v: boolean) => void
  reservations: ReservationType[]
  accountsReceivableSettings: AccountsReceivableSettingType[]
  currentTabReservationId: string
  onUpdateUsageDetails: () => void | Promise<void>
  onRefreshSalesData: () => void | Promise<void>
  receiptProps?: {
    customerId: string
    customerName: string
    customerLink?: any
    setIsReceiptModalOpen: (v: boolean) => void
    setIsSplitReceiptModalOpen: (v: boolean) => void
  }
  filterGuestRoomAssignId: string[] | undefined
  setFilterGuestRoomAssignId: (v: string[] | undefined) => void
  advanceCompany: AdvanceCompanyType
}

export const UsageDetails: React.FC<UsageDetailsProps> = ({
  checkinId,
  salesPayments,
  salesSubjects,
  salesSubjectSets,
  salesSubjectTaxes,
  totalUnpaid,
  originUsageDetails,
  usageDetails,
  setUsageDetails,
  editUsageDetails,
  setEditUsageDetails,
  confirmedDay,
  useCheckinDay,
  setIsLoading,
  reservations,
  accountsReceivableSettings,
  currentTabReservationId,
  onUpdateUsageDetails,
  onRefreshSalesData,
  receiptProps,
  advanceCompany,
  ...props
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const dispatch = useAppDispatch()
  const [deleteIds, setDeleteIds] = useState<{ id: string | undefined }[]>([])
  const [salesSubjectSetId, setSalesSubjectSetId] = useState<string>('')
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const { guardSalesEdit, createGuardedSetEditMode } = useSalesEditGuard()
  const setEditModeWithGuard = createGuardedSetEditMode(setIsEditMode)
  const { account, permissions, permissionsLoaded } = useContext<any>(AccountContext)
  const canPostClosingEdit = canUseOperation(permissions, 'post_closing_sales_edit')
  const canFetchPaymentSetting = hasManagerRole(account) || canEditScreen(permissions, 'sales')
  const [postClosingEditingIndex, setPostClosingEditingIndex] = useState<number | null>(null)
  const [postClosingEditingSnapshot, setPostClosingEditingSnapshot] = useState<SalesType | null>(null)
  const [postClosingPendingDelete, setPostClosingPendingDelete] = useState(false)
  const [isPostClosingModalOpen, setIsPostClosingModalOpen] = useState(false)
  const [deletedUsageDetails, setDeletedUsageDetails] = useState<any>([])
  /** 宿泊税追加で減額した101の、減額前スナップショット（税行全削除時に復元） */
  const [accommodationTaxRoomChargeBackup, setAccommodationTaxRoomChargeBackup] = useState<
    Record<
      string,
      {
        salesSubjectPrice: SalesType['salesSubjectPrice']
        quantity: number
        salesPrice: number
        unpaid: number
        salesPaymentPrice?: number
        isEdit?: boolean
      }
    >
  >({})

  useEffect(() => {
    dispatch(
      editingSectionActions.setEditingSection({
        key: 'sales',
        value: isEditMode || postClosingEditingIndex !== null,
      }),
    )
    return () => {
      dispatch(editingSectionActions.setEditingSection({ key: 'sales', value: false }))
    }
  }, [isEditMode, postClosingEditingIndex, dispatch])
  const [showSubjectSetSelect, setShowSubjectSetSelect] = useState<boolean>(false)
  const [isOpenInvoiceModal, setIsOpenInvoiceModal] = useState<boolean>(false)
  const [paymentSetting, setPaymentSetting] = useState<ModelPaymentSettingType>()
  const [paymentLinkIssueResult, setPaymentLinkIssueResult] = useState<{ amount: number; url: string }>()
  const { openConfirmDialog, ConfirmDialog } = useConfirmModal()
  const isPostClosingEditing = postClosingEditingIndex !== null
  const hasFullAssignedRoom =
    reservations?.length &&
    !reservations.some(
      reservation => !reservation?.assignedRoom?.length || reservation.assignedRoom.some(assignedRoom => !assignedRoom.guestRoomAssignId),
    )

  const hasUnpaidSales = useMemo(() => usageDetails?.some(i => !i.isPaid), [usageDetails])

  const showAccommodationTaxButton = salesSubjectTaxes.length > 0

  // 保存済み明細のみ。科目追加・科目セットの未保存行では宿泊税を有効にしない
  const isRoomChargeSales = (sales: { salesSubjectCode?: string | number; salesDate?: string }) =>
    Boolean(sales.salesDate) && String(sales.salesSubjectCode ?? '') === ROOM_CHARGE_SUBJECT_CODE

  const roomChargeSales = useMemo(() => usageDetails.filter(isRoomChargeSales), [usageDetails])

  // 101が締め固定なら無効。hasSentPosのみのグレー（スマレジ）は isPostClosingTarget に含まれないので有効のまま
  const hasPostClosingFixedRoomCharge = useMemo(
    () => usageDetails.some(sales => isRoomChargeSales(sales) && isPostClosingTarget(sales)),
    [usageDetails],
  )

  const canAddAccommodationTax = Boolean(hasFullAssignedRoom) && roomChargeSales.length > 0 && !hasPostClosingFixedRoomCharge

  // Editing newly created courses
  const changeDate = (date, index) => {
    if (!date) return
    const nextSalesDate = dayjs(date).format('YYYY-MM-DD')
    if (!confirmClosedSalesDateSelection(nextSalesDate, editUsageDetails[index].salesDate, confirmedDay, t)) return
    const _editUsageDetails = [...editUsageDetails]
    _editUsageDetails[index].salesDate = nextSalesDate
    addIsEdit(_editUsageDetails[index])
    setEditUsageDetails(_editUsageDetails)
  }
  const changeState = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>, index: number) => {
    const _editUsageDetails = [...editUsageDetails]
    const value = e.target.value
    const name = e.target.name
    if (name === 'isPaid') {
      // Changes to "Unsettled" and "Settled"
      _editUsageDetails[index]['isPaid'] = Boolean(Number(value))
      // Change payment: Change to "0" for "unsettled" and "sales amount (unit price * quantity)" for "settled"
      const paymentPrice =
        value === '0' ? 0 : Number(_editUsageDetails[index]['salesSubjectPrice']) * (_editUsageDetails[index]['quantity'] || 0)
      _editUsageDetails[index]['salesPaymentPrice'] = paymentPrice
    } else {
      _editUsageDetails[index][name] = value
    }

    // Change 'Unit Price' or 'Quantity' && update payment amount if sales is 'settled'
    if ((name === 'salesSubjectPrice' || name === 'quantity') && !!_editUsageDetails[index]['isPaid']) {
      _editUsageDetails[index]['salesPaymentPrice'] =
        Number(_editUsageDetails[index]['salesSubjectPrice']) * (_editUsageDetails[index]['quantity'] || 0)
    }

    // If there is an account receivable associated with the payment method, assign the account receivable ID of usageDetail together
    if (name === 'salesPaymentId') {
      autoCompleteAccountsReceivableWithSelectedSalesPayment(_editUsageDetails[index], value)
    }

    addIsEdit(_editUsageDetails[index])
    setEditUsageDetails(_editUsageDetails)
    // If there is a null salesPaymentId value in _editUsageDetails, hyphenate it there
    if (_editUsageDetails[index]['salesPaymentId'] === '') {
      _editUsageDetails[index]['salesPaymentId'] = salesPayments?.[0]?.['id']
    }
  }
  const changeEditQuantity = (index: number, quantity: number) => {
    const _editUsageDetails = [...editUsageDetails]
    _editUsageDetails[index].quantity = quantity
    if (_editUsageDetails[index].isPaid) {
      _editUsageDetails[index].salesPaymentPrice = Number(_editUsageDetails[index].salesSubjectPrice) * (quantity || 0)
    }
    addIsEdit(_editUsageDetails[index])
    setEditUsageDetails(_editUsageDetails)
  }
  // Change the date of a saved course
  const changeNotConfirmedDate = (date, index) => {
    if (!date) return
    const nextSalesDate = dayjs(date).format('YYYY-MM-DD')
    if (!confirmClosedSalesDateSelection(nextSalesDate, usageDetails[index].salesDate, confirmedDay, t)) return
    const _usageDetails = [...usageDetails]
    _usageDetails[index].salesDate = nextSalesDate
    addIsEdit(_usageDetails[index])
    setUsageDetails(_usageDetails)
  }
  // Edit unconfirmed course
  const changeNotConfirmedState = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>, index) => {
    const _usageDetails = [...usageDetails]
    const value = e.target.value
    const name = e.target.name
    if (name === 'isPaid') {
      // Changes to "Unsettled" and "Settled"
      _usageDetails[index]['isPaid'] = Boolean(Number(value))
      // Change payment: Change to "0" for "unsettled" and "sales amount (unit price * quantity)" for "settled"
      const paymentPrice = value === '0' ? 0 : Number(_usageDetails[index]['salesSubjectPrice']) * (_usageDetails[index]['quantity'] || 0)
      _usageDetails[index]['salesPaymentPrice'] = paymentPrice
    } else {
      _usageDetails[index][name] = value
    }

    if (name === 'salesSubjectId') {
      _usageDetails[index]['salesSubSubjectId'] = ''
      _usageDetails[index]['salesSubjectSetId'] = null
    }

    // Change 'Unit Price' or 'Quantity' && update payment amount if sales is 'settled'
    if ((name === 'salesSubjectPrice' || name === 'quantity') && !!_usageDetails[index]['isPaid']) {
      _usageDetails[index]['salesPaymentPrice'] =
        Number(_usageDetails[index]['salesSubjectPrice']) * (_usageDetails[index]['quantity'] || 0)
    }
    // If there is an account receivable associated with the payment method, assign the account receivable ID of usageDetail together
    if (name === 'salesPaymentId') {
      autoCompleteAccountsReceivableWithSelectedSalesPayment(_usageDetails[index], value)
    }
    if (name === 'companyId' && value) {
      autoCompleteAccountsReceivableWithSelectedSalesPayment(_usageDetails[index], value)
    }

    addIsEdit(_usageDetails[index])
    setUsageDetails(_usageDetails)
  }

  const changeNotConfirmedQuantity = (index: number, quantity: number) => {
    const _usageDetails = [...usageDetails]
    _usageDetails[index].quantity = quantity
    if (_usageDetails[index].isPaid) {
      _usageDetails[index].salesPaymentPrice = Number(_usageDetails[index].salesSubjectPrice) * (quantity || 0)
    }
    addIsEdit(_usageDetails[index])
    setUsageDetails(_usageDetails)
  }

  const markPostClosingForDelete = useCallback(() => {
    setPostClosingPendingDelete(true)
  }, [])

  const cancelPostClosingEdit = useCallback(() => {
    if (postClosingEditingIndex !== null && postClosingEditingSnapshot) {
      const restored = [...usageDetails]
      restored[postClosingEditingIndex] = { ...postClosingEditingSnapshot }
      setUsageDetails(restored)
    }
    setPostClosingEditingIndex(null)
    setPostClosingEditingSnapshot(null)
    setPostClosingPendingDelete(false)
    setIsPostClosingModalOpen(false)
  }, [postClosingEditingIndex, postClosingEditingSnapshot, usageDetails, setUsageDetails])

  const submitPostClosingAdjustment = useCallback(
    async (modalValues?: PostClosingModalSubmitValues) => {
      if (postClosingEditingIndex === null) return
      const sale = usageDetails[postClosingEditingIndex]
      // 編集後の状態ではなく、編集開始時（origin）で対象判定する（精算済→未精算でも弾かない）
      const originForContext = postClosingEditingSnapshot ?? sale
      if (!canUsePostClosingSalesApi(originForContext)) {
        alert(t('HotelPms.PostClosing.notPostClosingContext'))
        return
      }
      const payload = postClosingPendingDelete
        ? buildPostClosingDeleteParams(sale.id)
        : buildPostClosingSalesAdjustmentParams(sale, modalValues, postClosingEditingSnapshot ?? undefined)

      setIsLoading(true)
      try {
        await api.postPostClosingSalesAdjustment(payload)
        try {
          await onRefreshSalesData()
        } finally {
          setPostClosingEditingIndex(null)
          setPostClosingEditingSnapshot(null)
          setPostClosingPendingDelete(false)
          setIsPostClosingModalOpen(false)
        }
      } catch (error: any) {
        if (isStaffGuardCancelledError(error)) return
        const postClosingMessage = getPostClosingAdjustmentAlertMessage(error, t)
        if (postClosingMessage) {
          alert(postClosingMessage)
          return
        }
        errorHandler(error)
      } finally {
        setIsLoading(false)
      }
    },
    [
      postClosingEditingIndex,
      postClosingEditingSnapshot,
      postClosingPendingDelete,
      usageDetails,
      setIsLoading,
      onRefreshSalesData,
      t,
      errorHandler,
    ],
  )

  const savePostClosingEdit = useCallback(() => {
    if (postClosingEditingIndex === null) return

    if (postClosingPendingDelete) {
      void submitPostClosingAdjustment()
      return
    }

    const sale = usageDetails[postClosingEditingIndex]

    if (!isPostClosingRowInputValid(sale)) {
      alert(
        t('Please select the subject payment method and accommodation ID Please enter the unit price and quantity in single-byte numbers'),
      )
      return
    }

    if (sale.isPaid && hasPostClosingPaymentDatetime(sale)) {
      setIsPostClosingModalOpen(true)
      return
    }

    void submitPostClosingAdjustment()
  }, [postClosingEditingIndex, postClosingPendingDelete, usageDetails, t, submitPostClosingAdjustment])

  const restoreUsageDetailsFromOrigin = useCallback(() => {
    return filterUsageDetailsByGuestRoom(originUsageDetails, props.filterGuestRoomAssignId)
  }, [originUsageDetails, props.filterGuestRoomAssignId])

  const clearNormalEditState = useCallback(() => {
    setUsageDetails(restoreUsageDetailsFromOrigin())
    setEditUsageDetails([])
    setDeleteIds([])
    setDeletedUsageDetails([])
    setIsEditMode(false)
    setSalesSubjectSetId('')
    setShowSubjectSetSelect(false)
    setAccommodationTaxRoomChargeBackup({})
  }, [restoreUsageDetailsFromOrigin, setUsageDetails, setEditUsageDetails])

  const restoreAccommodationTaxRoomCharges = useCallback(() => {
    if (Object.keys(accommodationTaxRoomChargeBackup).length === 0) return
    const backup = accommodationTaxRoomChargeBackup
    setUsageDetails(
      usageDetails.map(detail => {
        const snap = backup[detail.id]
        if (!snap) return detail
        return {
          ...detail,
          salesSubjectPrice: snap.salesSubjectPrice,
          quantity: snap.quantity,
          salesPrice: snap.salesPrice,
          unpaid: snap.unpaid,
          ...(snap.salesPaymentPrice !== undefined ? { salesPaymentPrice: snap.salesPaymentPrice } : {}),
          isEdit: snap.isEdit,
        }
      }),
    )
    setAccommodationTaxRoomChargeBackup({})
  }, [accommodationTaxRoomChargeBackup, usageDetails, setUsageDetails])

  const startPostClosingEdit = useCallback(
    (index: number) => {
      const saleId = usageDetails[index]?.id
      const originSale = saleId ? originUsageDetails.find(item => item.id === saleId) : usageDetails[index]
      if (!originSale) return
      clearNormalEditState()
      const restored = restoreUsageDetailsFromOrigin()
      const displayIndex = saleId ? restored.findIndex(item => item.id === saleId) : index
      if (displayIndex < 0) return
      setPostClosingEditingSnapshot({ ...originSale })
      setPostClosingEditingIndex(displayIndex)
      setPostClosingPendingDelete(false)
    },
    [usageDetails, originUsageDetails, clearNormalEditState, restoreUsageDetailsFromOrigin],
  )

  const changePostClosingDate = (date: moment.Moment | null, index: number) => {
    if (!date) return
    const nextSalesDate = date.format('YYYY-MM-DD')
    if (!confirmClosedSalesDateSelection(nextSalesDate, usageDetails[index].salesDate, confirmedDay, t)) return
    const _usageDetails = [...usageDetails]
    _usageDetails[index].salesDate = nextSalesDate
    setUsageDetails(_usageDetails)
  }

  const changePostClosingState = (e: { target: { value: string; name: string } }, index: number) => {
    const _usageDetails = [...usageDetails]
    const value = e.target.value
    const name = e.target.name
    if (name === 'isPaid') {
      _usageDetails[index]['isPaid'] = Boolean(Number(value))
      const paymentPrice = value === '0' ? 0 : Number(_usageDetails[index]['salesSubjectPrice']) * _usageDetails[index]['quantity']
      _usageDetails[index]['salesPaymentPrice'] = paymentPrice
    } else {
      _usageDetails[index][name] = value
    }

    if (name === 'salesSubjectId') {
      _usageDetails[index]['salesSubSubjectId'] = ''
      _usageDetails[index]['salesSubjectSetId'] = null
    }

    if ((name === 'salesSubjectPrice' || name === 'quantity') && !!_usageDetails[index]['isPaid']) {
      _usageDetails[index]['salesPaymentPrice'] = Number(_usageDetails[index]['salesSubjectPrice']) * _usageDetails[index]['quantity']
    }
    if (name === 'salesPaymentId' || name === 'companyId') {
      autoCompleteAccountsReceivableWithSelectedSalesPayment(_usageDetails[index], value)
    }

    _usageDetails[index]['salesPrice'] = Number(_usageDetails[index]['salesSubjectPrice']) * Number(_usageDetails[index]['quantity'])
    setUsageDetails(_usageDetails)
  }

  const changePostClosingQuantity = (index: number, quantity: number) => {
    const _usageDetails = [...usageDetails]
    _usageDetails[index].quantity = quantity
    if (_usageDetails[index].isPaid) {
      _usageDetails[index].salesPaymentPrice = Number(_usageDetails[index].salesSubjectPrice) * (quantity || 0)
    }
    _usageDetails[index].salesPrice = Number(_usageDetails[index].salesSubjectPrice) * quantity
    setUsageDetails(_usageDetails)
  }

  const autoCompleteAccountsReceivableWithSelectedSalesPayment = (
    usageDetail: SalesType | EditSalesType,
    selectedSalesPaymentOrCompanyId: string,
  ) => {
    const selectedSalesPayment = salesPayments.find(salesPayment => {
      return salesPayment.id === selectedSalesPaymentOrCompanyId
    })
    const selectedCompany = advanceCompany?.paymentCompanies?.find(company => {
      return company.id === selectedSalesPaymentOrCompanyId
    })
    const alignmentAccountReceivable = accountsReceivableSettings?.find(receivable => {
      return receivable.code === selectedSalesPayment?.methodCode || receivable.id === selectedCompany?.salesAccountsReceivableMasterId
    })
    usageDetail['salesAccountsReceivableMasterId'] = alignmentAccountReceivable ? alignmentAccountReceivable.id : ''
  }

  const addUsageDetail = () => {
    if (!guardSalesEdit()) return
    setIsEditMode(true)
    const currentReservation = reservations.find(reservation => reservation.reservationId === currentTabReservationId)
    const isStayOrCO =
      currentReservation?.approvedStatus === +ApprovedStatus.Stay || currentReservation?.approvedStatus === +ApprovedStatus.Checkout

    const newUsageDetails: EditSalesType = {
      accommodationId: '',
      salesDate: isStayOrCO ? dayjs().format('YYYY-MM-DD') : useCheckinDay,
      salesSubjectId: '',
      salesSubjectPrice: 0,
      quantity: 1,
      salesPaymentId: '',
      salesPaymentPrice: 0,
      isEdit: true,
      isPaid: false,
      reservationId: currentTabReservationId,
      checkinId,
      salesSubSubjectId: '',
      salesSubjectCode: '',
      salesSubSubjectCode: '',
      salesAccountsReceivableMasterId: '',
      salesAccountsReceivableName: '',
      guestRoomAssignId: reservations.find(r => r.reservationId === currentTabReservationId)?.assignedRoom[0]?.guestRoomAssignId,
    }
    setEditUsageDetails([...editUsageDetails, newUsageDetails])
  }

  const deleteUsageDetail = (index: number) => {
    const newDetails = [...usageDetails]
    setDeletedUsageDetails([...deletedUsageDetails, newDetails[index]])
    newDetails.splice(index, 1)
    setUsageDetails(newDetails)
    setDeleteIds([...deleteIds, { id: usageDetails[index].id }])
  }

  const deleteUsageDetailSubject = index => {
    const newSubjectDetails = [...editUsageDetails]
    const removed = newSubjectDetails[index]
    newSubjectDetails.splice(index, 1)
    setEditUsageDetails(newSubjectDetails)
    // 宿泊税プレビュー由来の税行をすべて消したら、101減額も戻す
    if (removed?.fromAccommodationTax && !newSubjectDetails.some(detail => detail.fromAccommodationTax)) {
      restoreAccommodationTaxRoomCharges()
    }
  }

  // Newly created and edited item flags
  const addIsEdit = (array: SalesType | EditSalesType) => {
    const usageDetailById = originUsageDetails.find(originUsageDetail => originUsageDetail.id === array.id)
    const usageDetailsWithoutIsEdit = { ...array }
    delete usageDetailsWithoutIsEdit['isEdit']
    if (isSalesSubjectChange(usageDetailById, usageDetailsWithoutIsEdit)) {
      array['isEdit'] = true
    } else {
      array['isEdit'] = false
    }
  }

  const isDirtySalesOnConfirmed = (originSales: SalesType, editSales: EditSalesType | SalesType): boolean => {
    if (!originSales.isConfirm) {
      return false
    }
    return (
      originSales.salesDate !== editSales.salesDate ||
      Number(originSales.salesSubjectPrice) !== Number(editSales.salesSubjectPrice) ||
      Number(originSales.quantity) !== Number(editSales.quantity)
    )
  }

  const saveUsageDetail = async () => {
    if (!guardSalesEdit()) return
    // Deep copy modified and newly created data
    const _editUsageDetails = [...usageDetails, ...editUsageDetails].map(value => ({ ...value }))
    // Saved array: data that has not been changed or newly created is deleted from the array
    const saveUsageDetails = _editUsageDetails.filter(sales => sales['isEdit'] === true)
    let isEditConfirmedSales = false

    // Edit confirmed data → Add to history array, Edit unconfirmed data && Edit deposit → Set deposit date to null
    originUsageDetails.forEach(originSales => {
      if (deleteIds.findIndex(({ id }) => id === originSales.id) !== -1 && originSales.isConfirm) {
        isEditConfirmedSales = true
        return
      }
      // Get the edit item index with the same id as "original data id"
      const indexOfSameId = saveUsageDetails.findIndex(sales => sales.id === originSales.id)
      if (indexOfSameId === -1) return
      isEditConfirmedSales = isEditConfirmedSales || isDirtySalesOnConfirmed(originSales, saveUsageDetails[indexOfSameId])
      if (saveUsageDetails[indexOfSameId]['isPaid'] !== originSales.isPaid) {
        // Set the payment date (paymentSalesManagerDailyId) to null when editing pending items and changing "settled" or "unsettled"
        saveUsageDetails[indexOfSameId]['paymentSalesManagerDailyId'] = null
      }
    })

    if (!isItemDatesInStayPeriod(saveUsageDetails as SalesType[], reservations) && !(await openConfirmDialog())) {
      return
    }

    // If the saved data is caught in validation, display an alert and end the saving process
    if (checkCompletedInput(saveUsageDetails)) {
      alert(
        t('Please select the subject payment method and accommodation ID Please enter the unit price and quantity in single-byte numbers'),
      )
      return
    }

    // If the date of the saved data has been confirmed, add it to the history array
    saveUsageDetails.forEach(usageDetail => {
      usageDetail['salesSubjectPrice'] = Number(usageDetail['salesSubjectPrice']) || 0
      usageDetail['quantity'] = Number(usageDetail['quantity']) || 0
    })

    // Combining "create new, modify" and "delete" (data sent to API)
    const updateUsageDetails = [...saveUsageDetails, ...deleteIds]

    // If there is no data, exit edit mode and end save processing
    if (updateUsageDetails.length === 0) {
      setIsEditMode(false)
      return
    }

    if (
      isEditConfirmedSales &&
      !window.confirm(t('The date on which the closing process was closed. Sales totals will change, do you want to save them?'))
    ) {
      return
    }

    const adminSalesPayload = buildAdminSalesSavePayload(saveUsageDetails as SalesType[], deleteIds, originUsageDetails)

    if (adminSalesPayload.length === 0) {
      setIsEditMode(false)
      return
    }

    setIsLoading(true)
    api
      .updateSales(adminSalesPayload)
      .then(async () => {
        setDeletedUsageDetails([])
        setAccommodationTaxRoomChargeBackup({})
        try {
          await onUpdateUsageDetails()
        } finally {
          setIsLoading(false)
          setEditUsageDetails([])
          setDeleteIds([])
          setIsEditMode(false)
        }
      })
      .catch(error => {
        if (isStaffGuardCancelledError(error)) {
          setIsLoading(false)
          return
        }
        if (error?.response?.data?.errorCode === NOT_ENOUGH_BALANCE_CODE) {
          alert(t('Insufficient balance Please check again'))
        } else {
          console.error('admin-sales PUT failed', error?.response?.status, error?.response?.data)
          console.log(t('Communication failed'))
          setEditUsageDetails([])
          setIsEditMode(false)
        }
        setIsLoading(false)
      })
  }

  // Cancellation process
  const resetUsageDetails = () => {
    clearNormalEditState()
  }

  // Are the subject and payment method entered?
  const checkCompletedInput = editUsageDetails => {
    const isEmptySalesSubjectId = editUsageDetails.some(editUsageDetail => editUsageDetail.salesSubjectId === '')
    const isEmptySalesPaymentId = editUsageDetails.some(editUsageDetail => editUsageDetail.salesPaymentId === '')
    const isEmptySalesSubjectReservationId = editUsageDetails.some(
      editUsageDetail => editUsageDetail.reservationId === '' || editUsageDetail.reservationId == null,
    )
    // Unit price: salesSubjectPrice
    const isHalfSizeNumberSalesSubjectPrice = editUsageDetails.some(
      editUsageDetail => !String(editUsageDetail.salesSubjectPrice).match(/^-?[0-9]+$/),
    )
    // Quantity : quantity
    const isHalfSizeNumberQuantity = editUsageDetails.some(editUsageDetail => !String(editUsageDetail.quantity).match(/^-?[0-9]+$/))
    if (
      isEmptySalesSubjectId ||
      isEmptySalesPaymentId ||
      isEmptySalesSubjectReservationId ||
      isHalfSizeNumberSalesSubjectPrice ||
      isHalfSizeNumberQuantity
    ) {
      return true
    } else {
      return false
    }
  }

  const createSubjectSet = async () => {
    try {
      setIsLoading(true)

      const res = await api.fetchSalesAccountSubjectSet({ checkinId, reservationId: currentTabReservationId, salesSubjectSetId })

      setEditUsageDetails([
        ...editUsageDetails,
        ...res.salesGroupBySalesDate.map((i): EditSalesType => ({
          ...i,
          salesSubjectId: i.isDisplay ? i.salesSubjectId : '',
          salesSubjectName: i.isDisplay ? i.salesSubjectName : '',
          isEdit: true,
          salesPaymentId: salesPayments?.[0]?.id,
          salesPaymentPrice: 0,
          salesAccountsReceivableMasterId: '',
          salesAccountsReceivableName: '',
          salesSubSubjectId: (i.isDisplay && i.salesSubSubjectId) || '',
          salesSubSubjectName: (i.isDisplay && i.salesSubSubjectName) || '',
          salesSubSubjectCode: (i.isDisplay && i.salesSubSubjectCode) || '',
          isPaid: false,
          salesSubjectSetId,
        })),
      ])
      setIsEditMode(true)
      cancelSubjectSet()
      const hasDeletedSetItem = res.salesGroupBySalesDate.some(sales => !sales.isDisplay)
      if (hasDeletedSetItem) {
        window.alert(t('Subjects in the subject set have been changed\nPlease update your subject set'))
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const createAccommodationTax = async () => {
    if (!canAddAccommodationTax) return
    if (!guardSalesEdit()) return

    try {
      setIsLoading(true)
      const res = await api.fetchSalesAccountSubjectTax({
        checkinId,
        reservationId: currentTabReservationId,
      })

      const previewItems = res?.salesGroupBySalesDate ?? []
      const createItems = previewItems.filter(item => item.action !== 'update')
      const updateItems = previewItems.filter(item => item.action === 'update')
      const usageDetailsById = new Map(usageDetails.map(detail => [detail.id, detail]))

      // 精算済101への update がある場合のみ確認（force なし・再呼び出しなし）
      const paidUpdateTargets = updateItems
        .map(item => (item.id ? usageDetailsById.get(item.id) : undefined))
        .filter((detail): detail is SalesType => Boolean(detail?.isPaid))
      if (paidUpdateTargets.length > 0) {
        const roomChargeSubjectName = paidUpdateTargets[0].salesSubjectName || ROOM_CHARGE_SUBJECT_CODE
        const paidUpdateIds = new Set(paidUpdateTargets.map(detail => detail.id))
        const includedInPlanTaxSubjectIds = new Set(
          salesSubjectTaxes.filter(tax => tax.isIncludeInReservationPrice).map(tax => tax.salesSubject.salesSubjectMasterId),
        )
        const taxSubjectName =
          Array.from(
            new Set(
              createItems
                .filter(item => {
                  if (!item.salesSubjectId || !includedInPlanTaxSubjectIds.has(item.salesSubjectId)) {
                    return false
                  }
                  if (item.sourceRoomChargeSalesId) {
                    return paidUpdateIds.has(item.sourceRoomChargeSalesId)
                  }
                  return true
                })
                .map(item => item.salesSubjectName)
                .filter(Boolean),
            ),
          ).join('、') || t('saleSubjectTax.Tax')
        setIsLoading(false)
        const confirmed = await openConfirmDialog({
          message: t('Settled {{roomChargeSubjectName}} sales will be reduced by {{taxSubjectName}}. Do you want to proceed?', {
            roomChargeSubjectName,
            taxSubjectName,
          }),
          confirmLabel: t('OperationLog.action.execute'),
          cancelLabel: t('Cancel'),
          width: 510,
          height: 200,
        })
        if (!confirmed) {
          return
        }
        setIsLoading(true)
      }

      if (updateItems.length > 0) {
        const nextBackup = { ...accommodationTaxRoomChargeBackup }
        const nextUsageDetails = usageDetails.map(detail => {
          const updateItem = updateItems.find(item => item.id === detail.id)
          if (!updateItem) return detail
          // 減額前の値を保持（同じ行は初回のみ）。税行全削除時にここへ戻す
          if (!nextBackup[detail.id]) {
            nextBackup[detail.id] = {
              salesSubjectPrice: detail.salesSubjectPrice,
              quantity: detail.quantity,
              salesPrice: detail.salesPrice,
              unpaid: detail.unpaid,
              salesPaymentPrice: detail.salesPaymentPrice,
              isEdit: detail.isEdit,
            }
          }
          const salesSubjectPrice = Number(updateItem.salesSubjectPrice) || 0
          const quantity = Number(updateItem.quantity) || 0
          const salesPrice = salesSubjectPrice * quantity
          const nextSalesPaymentPrice =
            updateItem.salesPaymentPrice !== undefined ? Number(updateItem.salesPaymentPrice) || 0 : detail.salesPaymentPrice
          return {
            ...detail,
            salesSubjectPrice,
            quantity,
            salesPrice,
            unpaid: detail.isPaid ? detail.unpaid : salesPrice,
            ...(updateItem.salesPaymentPrice !== undefined ? { salesPaymentPrice: nextSalesPaymentPrice } : {}),
            isEdit: true,
          }
        })
        setAccommodationTaxRoomChargeBackup(nextBackup)
        setUsageDetails(nextUsageDetails)
      }

      if (createItems.length > 0) {
        setEditUsageDetails([
          ...editUsageDetails,
          ...createItems.map((i): EditSalesType => {
            const salesPaymentPrice = i.salesPaymentPrice ?? 0
            const sourceRoomCharge = i.sourceRoomChargeSalesId ? usageDetailsById.get(i.sourceRoomChargeSalesId) : undefined
            const salesPaymentId = i.salesPaymentId ?? sourceRoomCharge?.salesPaymentId ?? salesPayments?.[0]?.id ?? ''
            const companyId = sourceRoomCharge?.companyId ?? ''
            const salesAccountsReceivableMasterId =
              i.salesAccountsReceivableMasterId ?? sourceRoomCharge?.salesAccountsReceivableMasterId ?? ''
            const salesAccountsReceivableName =
              accountsReceivableSettings.find(receivable => receivable.id === salesAccountsReceivableMasterId)?.name ?? ''
            return {
              checkinId: i.checkinId || checkinId,
              reservationId: i.reservationId || currentTabReservationId,
              accommodationId: i.accommodationId || '',
              guestRoomAssignId: i.guestRoomAssignId,
              salesDate: i.salesDate || '',
              salesSubjectId: i.salesSubjectId || '',
              salesSubjectName: i.salesSubjectName,
              salesSubjectCode: i.salesSubjectCode || '',
              salesSubjectPrice: i.salesSubjectPrice ?? 0,
              quantity: i.quantity ?? 0,
              isEdit: true,
              salesPaymentId,
              companyId,
              salesPaymentPrice,
              salesAccountsReceivableMasterId,
              salesAccountsReceivableName,
              salesSubSubjectId: '',
              salesSubSubjectName: '',
              salesSubSubjectCode: '',
              isPaid: salesPaymentPrice > 0,
              fromAccommodationTax: true,
            }
          }),
        ])
      }

      setIsEditMode(true)
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.errorMessage
      if (apiMessage === 'No applicable accommodation tax') {
        window.alert(t('There are no accommodation taxes to add'))
        return
      }
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const cancelSubjectSet = () => {
    setSalesSubjectSetId('')
    setShowSubjectSetSelect(false)
  }

  const doOnlineBilling = async () => {
    if (!paymentSetting?.is_enabled_online_payment) {
      alert(
        t(
          'This service is free of charge, but requires prior application to the payment agent. Please contact our support team if you wish to use this service.',
        ),
      )
      return
    }
    if (!window.confirm(t('Do you want to issue a billing link?'))) {
      return
    }

    try {
      setIsLoading(true)
      const issueResult = await api.issueExternalLinkPayment({
        checkinId,
        reservationId: currentTabReservationId,
        filterGuestRoomAssignId: props.filterGuestRoomAssignId,
      })
      setPaymentLinkIssueResult(issueResult)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // 管理者 or sales 編集権限がある場合のみ取得（権限ロード待ちも考慮）
    if (!canFetchPaymentSetting) return
    if (!(hasManagerRole(account) || permissionsLoaded)) return
    api.fetchPaymentSetting().then(res => res && setPaymentSetting(res.settings))
  }, [account, canFetchPaymentSetting, permissionsLoaded])

  return (
    <>
      <div css={headerContainerStyle}>
        <div css={leftContainerStyle}>
          <div css={headerStyle}>{t('Usage details')}</div>
          {currentTabReservationId && totalUnpaid > 0 && <div css={rightTopStatusStyle}>{t('With accounts receivable')}</div>}
        </div>

        <div css={rightContainerStyle}>
          <RoomFilterButton reservations={reservations} onChange={props.setFilterGuestRoomAssignId} />
          {!!usageDetails?.length && (
            <>
              <DropDownButton
                buttonStyle={{
                  buttonType: ButtonType.NonStyle,
                  width: 78,
                  height: 32,
                  borderRadius: 23,
                  fontSize: 12,
                }}
                title={t('Bill')}
                items={[
                  { title: t('Issue invoice'), onClick: () => setIsOpenInvoiceModal(true) },
                  { title: t('Online billing'), onClick: () => doOnlineBilling(), disabled: !totalUnpaid },
                ]}
              />

              {receiptProps && currentTabReservationId && (
                <DropDownButton
                  dropDownStyle={css({ right: 0 })}
                  buttonStyle={{
                    buttonType: ButtonType.NonStyle,
                    width: 91,
                    height: 32,
                    borderRadius: 23,
                    fontSize: 12,
                  }}
                  title={t('receipt')}
                  items={[
                    ...(hasUnpaidSales
                      ? [
                          {
                            title: t('issue'),
                            icon: require('@/static/images/file_download.svg'),
                            onClick: () => receiptProps.setIsReceiptModalOpen(true),
                          },
                        ]
                      : []),
                    {
                      title: t('split issue'),
                      onClick: () => receiptProps.setIsSplitReceiptModalOpen(true),
                    },
                    {
                      title: t('issue history'),
                      onClick: () => {
                        if (checkinId) history.push(`/sales-manager/receipt-history/list?checkinId=${checkinId}`)
                        else if (receiptProps.customerLink?.cuicinUserId)
                          history.push(`/sales-manager/receipt-history/list?customerId=${receiptProps.customerLink?.cuicinUserId}`)
                        else if (currentTabReservationId) {
                          history.push(`/sales-manager/receipt-history/list?reservationId=${currentTabReservationId}`)
                        }
                      },
                    },
                  ]}
                />
              )}
            </>
          )}
        </div>
      </div>
      <table css={reservationDetailSalesTableStyle} style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th css={{ width: 96 }}>{t('Date')}</th>
            <th>
              {t('Subject')}/{t('Supplementary subjects')}
            </th>
            <th css={{ width: '12.5%' }}>{`${t('Room number')}(${t('Room ID')})`}</th>
            <th css={{ width: '7.5%' }}>{t('Unit price')}</th>
            <th css={{ width: '6%' }}>{t('Quantity')}</th>
            <th css={{ width: '6%' }}>{t('Earnings')}</th>
            <th css={{ width: '12%' }}>{t('Method of payment')}</th>
            <th css={{ width: 86 }}>{t('Payment')}</th>
            <th css={{ width: '12%' }}>{t('Accounts receivable')}</th>
            <th css={{ width: 46 }}></th>
          </tr>
        </thead>
        <tbody>
          {usageDetails &&
            currentTabReservationId &&
            usageDetails.map((usageDetail, index) => {
              if (!Object(usageDetail).salesDate || !currentTabReservationId || !accountsReceivableSettings) {
                return null
              }
              const isPostClosingEditingRow = postClosingEditingIndex === index
              const showPostClosingFixedRow = shouldShowPostClosingUsageDetailRow(usageDetail, isPostClosingEditingRow)
              const canEditThisPostClosingRow = canPostClosingEdit && canShowPostClosingEditPencil(usageDetail)
              return (
                <React.Fragment key={index}>
                  {!usageDetail.isEdit && showPostClosingFixedRow ? (
                    isPostClosingEditingRow ? (
                      <PostClosingConfirmedUsageDetail
                        key={index}
                        index={index}
                        usageDetail={usageDetail}
                        advanceCompany={advanceCompany}
                        isEditing={true}
                        canPostClosingEdit={canEditThisPostClosingRow}
                        onStartEdit={() => startPostClosingEdit(index)}
                        onMarkDelete={markPostClosingForDelete}
                        onCancelEdit={cancelPostClosingEdit}
                        onSaveEdit={savePostClosingEdit}
                        pendingDelete={postClosingPendingDelete}
                        salesSubjects={salesSubjects}
                        salesPayments={salesPayments}
                        confirmedDay={confirmedDay}
                        changePostClosingDate={changePostClosingDate}
                        changePostClosingState={changePostClosingState}
                        changePostClosingQuantity={changePostClosingQuantity}
                        accountsReceivableSettings={accountsReceivableSettings}
                        reservations={reservations}
                        currentTabReservationId={currentTabReservationId}
                      />
                    ) : (
                      <ConfirmedUsageDetail
                        key={index}
                        index={index}
                        usageDetail={usageDetail}
                        advanceCompany={advanceCompany}
                        canPostClosingEdit={canEditThisPostClosingRow}
                        onStartPostClosingEdit={() => startPostClosingEdit(index)}
                      />
                    )
                  ) : (
                    <NotConfirmedUsageDetail
                      key={index}
                      index={index}
                      advanceCompany={advanceCompany}
                      usageDetail={usageDetail}
                      salesSubjects={salesSubjects}
                      salesPayments={salesPayments}
                      confirmedDay={confirmedDay}
                      allowClosedDates={canPostClosingEdit}
                      isEditMode={isEditMode}
                      changeNotConfirmedDate={changeNotConfirmedDate}
                      changeNotConfirmedState={changeNotConfirmedState}
                      changeNotConfirmedQuantity={changeNotConfirmedQuantity}
                      deleteUsageDetail={deleteUsageDetail}
                      setIsEditMode={setEditModeWithGuard}
                      accountsReceivableSettings={accountsReceivableSettings}
                      reservations={reservations}
                      currentTabReservationId={currentTabReservationId}
                    />
                  )}
                </React.Fragment>
              )
            })}
          {/* Newly added subject */}
          {!isPostClosingEditing &&
            editUsageDetails &&
            currentTabReservationId &&
            editUsageDetails.map((editUsageDetail, index) => (
              <UsageDetailSubject
                key={index}
                index={index}
                editUsageDetail={editUsageDetail}
                advanceCompany={advanceCompany}
                salesSubjects={salesSubjects}
                salesPayments={salesPayments}
                confirmedDay={confirmedDay}
                allowClosedDates={canPostClosingEdit}
                changeDate={changeDate}
                changeState={changeState}
                changeQuantity={changeEditQuantity}
                deleteUsageDetailSubject={deleteUsageDetailSubject}
                accountsReceivableSettings={accountsReceivableSettings}
                reservations={reservations}
              />
            ))}
        </tbody>
      </table>

      <div css={borderStyle}>
        {currentTabReservationId && !isPostClosingEditing ? (
          <div css={addButtonsRowStyle}>
            {showSubjectSetSelect ? (
              <div css={subjectSetSelectRowStyle}>
                <select value={salesSubjectSetId} onChange={e => setSalesSubjectSetId(e.target.value)} css={selectStyle}>
                  <option value="" disabled>
                    {t('Please select')}
                  </option>
                  {salesSubjectSets.map(value => (
                    <option key={value.salesSubjectSetId} value={value.salesSubjectSetId}>
                      {value.salesSubjectSetName}
                    </option>
                  ))}
                </select>
                <div css={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={16} onClick={cancelSubjectSet}>
                    {t('Cancel')}
                  </Button>
                  <Button
                    buttonType={!salesSubjectSetId ? 5 : 1}
                    width={80}
                    height={32}
                    isDisabled={!salesSubjectSetId}
                    fontSize={12}
                    onClick={createSubjectSet}
                  >
                    {t('Create')}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                css={[addDashedButtonStyle, !hasFullAssignedRoom ? addButtonDisabledStyle : undefined]}
                onClick={hasFullAssignedRoom ? () => setShowSubjectSetSelect(true) : undefined}
              >
                <img
                  src={!hasFullAssignedRoom ? require('@/static/images/plus_gray.svg') : require('@/static/images/plus_yellow.svg')}
                  alt={t('Add icon yellow')}
                />
                <p>{t('Add subject set')}</p>
              </div>
            )}
            <div css={addDashedButtonStyle} onClick={addUsageDetail}>
              <img src={require('@/static/images/plus_yellow.svg')} alt={t('Add icon yellow')} />
              <p>{t('Add subject')}</p>
            </div>
            {showAccommodationTaxButton && !showSubjectSetSelect && (
              <div
                css={[addAccommodationTaxButtonStyle, !canAddAccommodationTax ? addButtonDisabledStyle : undefined]}
                aria-disabled={!canAddAccommodationTax}
                onClick={canAddAccommodationTax ? createAccommodationTax : undefined}
              >
                <img
                  src={!canAddAccommodationTax ? require('@/static/images/plus_gray.svg') : require('@/static/images/plus_yellow.svg')}
                  alt={t('Add icon yellow')}
                />
                <p>{t('Add accommodation tax')}</p>
              </div>
            )}
          </div>
        ) : (
          <p css={isEmptyReservationRoomListWrapperStyle}></p>
        )}
      </div>
      {isEditMode && !isPostClosingEditing && (
        <div css={saveButtonStyle}>
          <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={14} marginBottom={16} onClick={resetUsageDetails}>
            {t('Cancel')}
          </Button>
          <Button width={80} height={32} buttonType={1} fontSize={12} marginRight={32} marginBottom={16} onClick={saveUsageDetail}>
            {t('Save')}
          </Button>
        </div>
      )}

      {isOpenInvoiceModal && (
        <InvoiceDetailModal
          onClose={() => setIsOpenInvoiceModal(false)}
          initialReservations={reservations}
          filterGuestRoomAssignId={props.filterGuestRoomAssignId}
        />
      )}
      {paymentLinkIssueResult && (
        <OnlineBillingModal
          onClose={() => setPaymentLinkIssueResult(undefined)}
          billAmount={paymentLinkIssueResult?.amount}
          billLink={paymentLinkIssueResult?.url}
        />
      )}
      {isPostClosingModalOpen && postClosingEditingIndex !== null && (
        <PostClosingPaymentDateModal
          usageDetail={usageDetails[postClosingEditingIndex]}
          onClose={cancelPostClosingEdit}
          onSave={values => {
            void submitPostClosingAdjustment(values)
          }}
        />
      )}
      <ConfirmDialog />
    </>
  )
}

const headerStyle = css(sectionHeaderStyle, {
  height: 50,
  marginLeft: 16,
})

const headerContainerStyle = css({
  marginRight: 16,
  display: 'flex',
  justifyContent: 'space-between',
})

const leftContainerStyle = css({
  display: 'flex',
  float: 'left',
  alignItems: 'center',
})

const rightContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  gap: 8,
})

const rightTopStatusStyle = css({
  height: 25,
  minWidth: 102,
  border: '1px solid #F2A40B',
  borderRadius: 5,
  color: '#F2A40B',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '6px',
})

const saveButtonStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '24px',
  paddingBottom: '8px',
})

const borderStyle = css({
  borderTop: '1px solid ##F2F2F2',
  borderBottom: '1px solid #F2F2F2',
  marginBottom: 24,
})

const addRoomListWrapperStyle = css({
  cursor: 'pointer',
  display: 'flex',
  height: 36,
  backgroundColor: '#FAFAFA',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px dashed #CCCCCC',
  p: {
    lineHeight: '36px',
    color: '#F2A40B',
    fontSize: 14,
    fontWeight: 'bold',
    paddingLeft: 8,
    letterSpacing: '1.4px',
    whiteSpace: 'nowrap',
  },
})

const addButtonsRowStyle = css({
  display: 'flex',
  alignItems: 'stretch',
  gap: 16,
  minWidth: 0,
  boxSizing: 'border-box',
  margin: '32px 24px',
  '@media(max-width: 1080px)': {
    marginInline: 16,
  },
})

const addDashedButtonStyle = css(addRoomListWrapperStyle, {
  flex: '461 1 0',
  minWidth: 0,
  img: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
  ':hover': {
    background: '#F2F2F2',
  },
})

const addAccommodationTaxButtonStyle = css(addDashedButtonStyle, {
  flex: '167 1 0',
  maxWidth: 167,
  minWidth: 0,
})

const subjectSetSelectRowStyle = css({
  flex: '461 1 0',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
})

const addButtonDisabledStyle = css({
  cursor: 'not-allowed',
  pointerEvents: 'none',
  ':hover': {
    backgroundColor: '#FAFAFA',
  },
  p: {
    color: '#ccc !important',
  },
})

const isEmptyReservationRoomListWrapperStyle = css({
  display: 'flex',
  height: 36,
  aliginItemsCenter: 'center',
  margin: '32px 30px',
  p: {
    lineHeight: '36px',
    color: '#676767',
    fontSize: 14,
    paddingLeft: 8,
    letterSpacing: '1.4px',
  },
})

const selectStyle = css(initializeSelectBoxStyle, {
  width: '100%',
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundImage: `url(${require('@/static/images/arrow_yellow.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 5,
  backgroundPosition: '95% center',
})
